import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { useCurrentBoxId } from "@/modules/core/store/useCurrentSpaceManagerStore"
import { cn } from "@/modules/lib/utils"
import { useUser } from "@/modules/users/hooks/useUser"
import { useBoxNavigate } from "@modules/navigation/hooks/useBoxNavigate"
import { Path } from "@modules/navigation/routes"
import { ProfileAvatar } from "@modules/profile/common/components/profileAvatar"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { type HUImage } from "@modules/types/imageTypes"
import { HuFlag } from "@modules/ui/components/huFlag"
import { Mail, MessageCircleMore } from "lucide-react"
import { Link, useNavigate } from "react-router"

export type HuAvatarCardSidebarProps = {
  id?: string
  avatar?: HUImage
  firstname?: string
  lastname?: string
  username?: string
  nationality?: string
  email?: string
  logo?: React.JSX.Element
  className?: string
  skeletonClassName?: string
  style?: React.CSSProperties
  prefixUsername?: string
  onAvatarClick?: () => void
  canUseContact?: boolean
  canUseChatContact?: boolean
  topContent?: React.JSX.Element
  bottomButton?: React.JSX.Element
} & React.HTMLAttributes<HTMLDivElement>

/**
 * HuAvatarCardSidebar component renders a sidebar card with user avatar and details.
 * It displays a skeleton loader when data is being fetched.
 */
export function HuAvatarCardSidebar({
  id,
  avatar,
  firstname,
  lastname,
  username,
  nationality,
  email,
  className,
  prefixUsername,
  logo,
  onAvatarClick,
  canUseContact = true,
  canUseChatContact = true,
  bottomButton,
  topContent,
  ...props
}: HuAvatarCardSidebarProps) {
  const { hideSidebar } = useSidebar()
  const boxId = useCurrentBoxId()
  const boxNavigate = useBoxNavigate()
  const navigate = useNavigate()
  const user = useUser()

  const openChatWithUser = () => {
    if (id) {
      if (boxId) {
        hideSidebar()
        void boxNavigate(Path.Chat.Root, { state: { userToChatTo: id } })
      } else {
        hideSidebar()
        void navigate(Path.Chat.Root, { state: { userToChatTo: id } })
      }
    }
  }

  return (
    <div
      className={cn(
        "relative flex h-61 w-full flex-col items-center justify-center rounded-b-3xl bg-surface-foreground",
        className,
      )}
      {...props}
    >
      {/* TOP CONTENT */}
      {topContent}

      <div className="flex w-full items-center justify-center gap-3 pt-6">
        {/* PICTURE PROFIL */}
        <div className={cn("relative", onAvatarClick && "cursor-pointer")} onClick={onAvatarClick}>
          <ProfileAvatar
            className="mb-1 size-22"
            profile={{
              avatar: avatar ?? null,
              username: username ?? firstname ?? "",
            }}
          />
          {nationality && (
            <div className="absolute bottom-0" style={{ left: "40%" }}>
              <HuFlag
                className="rounded-lg border border-neutral-700"
                countryCode={nationality || ""}
                svg={true}
                style={{ width: "1.4rem", height: "1.2rem", objectFit: "cover" }}
              />
            </div>
          )}
        </div>

        {/* LOGO NAME PSEUDO */}
        <div
          className={cn("mr-4 flex max-w-80 flex-col items-center", onAvatarClick && "cursor-pointer")}
          onClick={onAvatarClick}
        >
          {logo}
          <div className="flex flex-col items-center justify-center">
            <Text
              fontStyle="HS"
              className="line-clamp-2 text-center wrap-anywhere whitespace-break-spaces text-textDefault-heading capitalize"
            >
              {firstname + " " + lastname}
            </Text>
            <Text
              fontStyle="BM"
              className="line-clamp-2 wrap-anywhere whitespace-break-spaces text-textDefault-caption capitalize"
            >
              {prefixUsername ?? ""}
              {username}
            </Text>
          </div>
        </div>
      </div>

      {/* BUTTOM BUTTON */}
      {bottomButton}

      {/* CONTACT ICONS */}
      {user && (user.id === id || !canUseContact) ? null : (
        <div className="absolute right-0 mt-8 mr-4 flex flex-col gap-1">
          {canUseChatContact && (
            <Button variant="outline" size="iconMd" onClick={() => void openChatWithUser()}>
              <MessageCircleMore />
            </Button>
          )}

          {email && (
            <Button variant="outline" size="iconMd">
              <Link
                to="#"
                onClick={(e) => {
                  window.location.href = `mailto:${email}`
                  e.preventDefault()
                }}
              >
                <Mail />
              </Link>
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
